import type { LOGIN_PAYLOAD } from "@sinch/bus";
import type { ConstructorFor } from "simplytyped";
import { type AuthConfig } from "../config/client";
import type { AuthStrategy, Token } from "./types";

const authStrategies: Record<
  AuthConfig["type"],
  () => Promise<ConstructorFor<AuthStrategy>>
> = {
  azure: () => import("./azure").then((mod) => mod.AzureAuthStrategy),
  kc: () => import("./keycloak").then((mod) => mod.KeycloakAuthStrategy),
  mm: () =>
    import("./messagemedia").then((mod) => mod.MessageMediaAuthStrategy),
  oidc: () => import("./oidc").then((mod) => mod.OidcAuthStrategy),
  tbi: () => import("./tbi").then((mod) => mod.TbiAuthStrategy),
  dashboard: () =>
    import("./sinchDashboard").then((mod) => mod.SinchDashboardAuthStrategy),
};

class AuthStrategyManager implements AuthStrategy {
  private strategy: AuthStrategy | null = null;

  async setup(authType: AuthConfig["type"]) {
    if (this.strategy) {
      throw new Error("Auth strategy already set up");
    }
    this.strategy = new (await authStrategies[authType]())();
  }

  async init() {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.init();
  }

  patchRequests = () => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.patchRequests();
  };
  login = (
    loginConfig?:
      | {
          redirectUri?: string;
        }
      | undefined
  ) => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.login(loginConfig);
  };
  logout = () => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.logout();
  };
  getToken = () => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.getToken();
  };
  getTokenP = () => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.getTokenP();
  };

  onTokenUpdate = (callback: (token: Token | null) => void) => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.onTokenUpdate(callback);
  };

  exchangeToken = (tokenPayload: LOGIN_PAYLOAD) => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.exchangeToken(tokenPayload);
  };
  forceInitWithExchangedToken = (token: {
    access_token: string;
    refresh_token: string;
  }) => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.forceInitWithExchangedToken(token);
  };
  getSession = () => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.getSession();
  };
  patchRefreshToken = (
    data: {
      redirectUrl?: string;
      accountId?: string;
      onPatchSuccess?: () => void;
    },
    options?:
      | {
          reason?: string;
          roles?: string[];
        }
      | undefined
  ) => {
    if (!this.strategy) {
      throw new Error("Auth strategy not set up");
    }
    return this.strategy.patchRefreshToken(data, options);
  };
}

export const authStrategy: AuthStrategyManager = new AuthStrategyManager();

export const setupAuth = async (
  type: AuthConfig["type"],
  patchRequests: boolean
) => {
  await authStrategy.setup(type);
  await authStrategy.init();
  if (patchRequests) {
    authStrategy.patchRequests();
  }
};
